import Vue from 'vue'
import html2canvas from 'html2canvas'

/**
 * @description: 检测元素是否超出父容器
    <div 
        v-checkOverParent="{
        index: index,
        parent: checkOverParent,
        handler: handleCheckOver,
        }"
    >
    </div>

    methdos: {
        checkOverParent() {
            return this.$refs.parentContainer
        },
        handleCheckOver(val, index) {}
    }

 */
Vue.directive('checkOverParent', {
    bind(el, binding) {
        el.__checkOverflow__ = event =>
            handleCheckOverParentFun(event, el, binding)
        window.addEventListener('resize', el.__checkOverflow__)
        const observer = new IntersectionObserver(
            () => {
                el.__checkOverflow__()
            },
            {
                threshold: 1.0 // 设置阈值为1，表示当元素完全进入视口时触发
            }
        )
        observer.observe(el) // 观察目标元素
        el.__observer__ = observer
    },
    unbind(el) {
        if (el.__observer__) {
            el.__observer__.disconnect()
            delete el.__observer__
        }
        window.removeEventListener('resize', el.__checkOverflow__)
        delete el.__checkOverflow__
    }
})

/**
 * 检查子元素是否超出父元素边界
 * @param {HTMLElement} child - 需要检查的子元素
 * @param {HTMLElement} [parent=child.parentElement] - 父元素，默认为子元素的直接父元素
 * @param {boolean} [checkX=true] - 是否检查水平方向的溢出
 * @param {boolean} [checkY=true] - 是否检查垂直方向的溢出
 * @returns {boolean|Object} 如果同时检查X和Y方向，返回布尔值；如果只检查一个方向，返回包含各方向溢出状态的对象
 */
function isElementOverflowingParent(
    child,
    parent = child.parentElement,
    checkX = true,
    checkY = true
) {
    if (
        !child ||
        !parent ||
        !child.getBoundingClientRect ||
        !parent.getBoundingClientRect
    ) {
        return false
    }

    // 获取元素边界信息
    const childRect = child.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()

    // 获取父元素的滚动位置
    const parentScrollLeft = parent.scrollLeft || 0
    const parentScrollTop = parent.scrollTop || 0

    // 计算相对于父元素的坐标
    const childLeft = childRect.left - parentRect.left + parentScrollLeft
    const childRight = childRect.right - parentRect.left + parentScrollLeft
    const childTop = childRect.top - parentRect.top + parentScrollTop
    const childBottom = childRect.bottom - parentRect.top + parentScrollTop

    // 判断是否溢出
    const isOverflowingLeft = childLeft < 0
    const isOverflowingRight = childRight > parentRect.width
    const isOverflowingTop = childTop < 0
    const isOverflowingBottom = childBottom > parentRect.height

    // 返回结果
    if (checkX && checkY) {
        return (
            isOverflowingLeft ||
            isOverflowingRight ||
            isOverflowingTop ||
            isOverflowingBottom
        )
    }
    if (checkX) {
        return { left: isOverflowingLeft, right: isOverflowingRight }
    }
    if (checkY) {
        return { top: isOverflowingTop, bottom: isOverflowingBottom }
    }

    return false
}

const handleCheckOverParentFun = (event, el, binding) => {
    if (binding?.value?.parent && binding?.value?.handler) {
        const parent = binding.value.parent()
        const isOverflowing = isElementOverflowingParent(el, parent)
        binding.value.handler(isOverflowing, binding?.value?.index)
    }
}

/**
 * @description: 检测元素滚动到什么位置
    <div 
      v-scrollListener="handleScroll" >
    ...
    </div>

    methdos: {
        handleScroll(value) {}
    }

 */
Vue.directive('scrollListener', {
    bind(el, binding) {
        el.__scrollListener__ = event =>
            handleScrollListener(event, el, binding)
        el.addEventListener('scroll', el.__scrollListener__)
    },
    unbind(el) {
        el.removeEventListener('scroll', el.__scrollListener__)
        delete el.__scrollListener__
    }
})
const handleScrollListener = (event, el, binding) => {
    const totalHeight = el.scrollHeight
    const visibleHeight = el.clientHeight
    const scrollTop = el.scrollTop
    const result = totalHeight - (scrollTop + visibleHeight) < 1
    if (result) {
        binding.value('bottom')
    } else if (scrollTop === 0) {
        binding.value('top')
    }
}

/**
 * @description: 检测元素是否超出屏幕
    <div 
      v-checkOverflow="handleCheckOverflow" >
    ...
    </div>

    methdos: {
        handleCheckOverflow(value) {}
    }

 */
Vue.directive('checkOverflow', {
    bind(el, binding) {
        el.__checkOverflow__ = event => handleCheckOverflow(event, el, binding)
        window.addEventListener('resize', el.__checkOverflow__)
        const observer = new IntersectionObserver(
            () => {
                el.__checkOverflow__()
            },
            {
                threshold: 1.0 // 设置阈值为1，表示当元素完全进入视口时触发
            }
        )
        observer.observe(el) // 观察目标元素
        el.__observer__ = observer
    },
    unbind(el) {
        if (el.__observer__) {
            el.__observer__.disconnect()
            delete el.__observer__
        }
        window.removeEventListener('resize', el.__checkOverflow__)
        delete el.__checkOverflow__
    }
})
const handleCheckOverflow = (event, el, binding) => {
    const rect = el.getBoundingClientRect()
    const inViewport =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
            (window.innerWidth || document.documentElement.clientWidth)
    binding.value(!inViewport)
}

/**
 * @description: 右键自定义菜单（复制）
    <div 
      v-customContextmenu="{
      options: contextmenuOptions,
      handler: handleContextmenu,
    }">
    ...
    </div>

    data() {
        return {
            contextmenuOptions: [
                { label: '复制', value: 'copy' },
                { label: '搜索', value: 'search' },
            ],
        }
    },

    methdos: {
        handleContextmenu(params) {}
    }

 */

Vue.directive('customContextmenu', {
    bind(el, binding) {
        el.__contextmenuListener__ = event =>
            handleContextmenuListener(event, el, binding)
        el.addEventListener('contextmenu', el.__contextmenuListener__)
        el.__contextmenuClickListener__ = event =>
            handleContextmenuClickListener(event, el, binding)
        document.addEventListener('click', el.__contextmenuClickListener__)
    },
    unbind(el) {
        el.removeEventListener('contextmenu', el.__contextmenuListener__)
        document.removeEventListener('click', el.__contextmenuClickListener__)
        removeCustomContextmenu()
    }
})
const handleContextmenuClickListener = () => {
    removeCustomContextmenu()
}

function getSelectedTextExcludingNone() {
    const selection = window.getSelection()
    if (selection.rangeCount === 0) return ''
    let result = ''

    for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i)
        const tempResult = processRange(range)
        result += tempResult
    }
    return result
}

function processRange(range) {
    let result = ''
    // 确定根节点为元素节点
    const root = getRangeCommonAncestorElement(range)
    if (!root) return result

    const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
            return range.intersectsNode(node)
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_REJECT
        }
    })

    while (treeWalker.nextNode()) {
        const textNode = treeWalker.currentNode
        if (isTextSelectable(textNode)) {
            const text = getTextInRange(textNode, range)
            result += text
        }
    }
    return result
}

// 获取选区公共祖先元素节点
function getRangeCommonAncestorElement(range) {
    let ancestor = range.commonAncestorContainer
    // 向上查找直到元素节点
    while (ancestor && ancestor.nodeType !== Node.ELEMENT_NODE) {
        ancestor = ancestor.parentElement
    }
    return ancestor
}

// 其他函数保持不变
function isTextSelectable(textNode) {
    let parent = textNode.parentElement
    while (parent) {
        if (isElementUserSelectNone(parent)) {
            return false
        }
        parent = parent.parentElement
    }
    return true
}

function isElementUserSelectNone(element) {
    const computedStyle = window.getComputedStyle(element)
    const userSelect =
        computedStyle.userSelect ||
        computedStyle.webkitUserSelect ||
        computedStyle.mozUserSelect ||
        computedStyle.msUserSelect
    return userSelect === 'none'
}

function getTextInRange(textNode, range) {
    const startOffset =
        textNode === range.startContainer ? range.startOffset : 0
    const endOffset =
        textNode === range.endContainer
            ? range.endOffset
            : textNode.textContent.length

    if (startOffset >= endOffset) return ''
    return textNode.textContent.slice(startOffset, endOffset)
}

const handleContextmenuListener = (event, el, binding) => {
    if (binding.value?.options) {
        event.preventDefault()
        // const selection = document.getSelection()
        // const context = selection.getRangeAt(0).toString()
        const context = getSelectedTextExcludingNone()
        if (context || binding.value?.type === 'container') {
            // context：选中触发；container：仅容器触发；
            removeCustomContextmenu()
            const maxLeft = window.innerWidth
            const maxTop = window.innerHeight
            const left = Math.max(0, Math.min(event.clientX, maxLeft))
            const top = event.clientY
            const items = []
            const options = binding?.value?.options
            if (binding.value.options.length > 0) {
                for (let index = 0; index < options.length; index++) {
                    const item = options[index]
                    let str = `<div class="contextmenu-item ${item.value}" id="${item.value}" >${item.label}</div>`
                    items.push(str)
                }
            }
            const dom = document.createElement('div')
            dom.className = 'custom-contextmenu'
            dom.setAttribute('id', 'custom-contextmenu')
            dom.innerHTML = items.join('')
            document.body.appendChild(dom)
            const domWidth = dom.offsetWidth
            const domHeight = dom.offsetHeight
            if (left > maxLeft - domWidth) {
                dom.style.top = `${top}px`
                dom.style.right = 0
                if (top > maxTop - domHeight) {
                    dom.style.top = 'auto'
                    dom.style.bottom = 0
                    dom.style.right = 0
                }
            } else {
                if (top > maxTop - domHeight) {
                    dom.style.left = `${left}px`
                    dom.style.bottom = 0
                } else {
                    dom.style.top = `${top}px`
                    dom.style.left = `${left}px`
                    dom.style.bottom = 'auto'
                }
            }
            dom.addEventListener('click', e => {
                const target = e.target
                if (target.getAttribute('id')) {
                    const type = target.getAttribute('id')
                    if (type === 'copy') {
                        if (context) {
                            copyContent(context)
                        } else if (
                            binding.value?.type === 'container' &&
                            event.target.src
                        ) {
                            // 复制图片
                            copyImg(event.target.src)
                        }
                    }
                    if (binding?.value?.handler) {
                        binding?.value?.handler({
                            type,
                            context
                        })
                    }
                    document.body.removeChild(dom)
                }
            })
        } else {
            removeCustomContextmenu()
        }
    }
}
const removeCustomContextmenu = () => {
    const dom = document.getElementById('custom-contextmenu')
    if (dom) {
        document.body.removeChild(dom)
    }
}

export const copyImg = src => {
    const img = document.createElement('img')
    img.src = src
    img.alt = ''
    img.className = ''
    if (typeof onLoad === 'function') {
        // img.onload
    } else {
        // 默认加载完成的回调
        img.onload = function () {
            console.log('图片加载成功:', src)
            html2canvas(img, {
                useCORS: true
                // backgroundColor: '#ececec',
            }).then(canvas => {
                // let canvasImg =
                //     canvas.toDataURL('image/png')
                // 兼容剪贴板复制
                canvas.toBlob(async blob => {
                    // 创建一个隐藏的URL
                    const url = URL.createObjectURL(blob)
                    // 创建一个Image对象，并将其src设置为Blob对象的URL
                    const img = new Image()
                    img.src = url
                    // 将图片添加到剪贴板
                    navigator.clipboard
                        .write([
                            new ClipboardItem({
                                'image/png': blob
                            })
                        ])
                        .then(res => {
                            console.log(res)
                        })
                        .catch(res => {
                            console.warn(res)
                        })
                    // 释放URL对象
                    URL.revokeObjectURL(url)
                })
                document.body.removeChild(img)
            })
        }
    }
    if (typeof onError === 'function') {
        // img.onerror
    } else {
        // 默认加载错误的回调
        img.onerror = function () {
            console.error('图片加载失败:', src)
        }
    }
    // 将图片添加到body中
    document.body.appendChild(img)
}

export const copyContent = context => {
    const input = document.createElement('textarea')
    input.value = context
    document.body.appendChild(input)
    input.select()
    input.setSelectionRange(0, 99999)
    document.execCommand('copy')
    document.body.removeChild(input)
}

/**
 * @description: 元素外部点击

  <div v-click-outside="handleClickOutside"></div>

  指定元素之外生效
  <div
    v-click-outside="{
      handler: handleClickOutside,
      include: include
    }"
  >
  </div>

include() {
    return [document.querySelector('.current_info')]
},
handleClickOutside() {
    this.$store.commit('updateOrgListVisible', false)
},

 */
Vue.directive('clickOutside', {
    // 指令的绑定函数，在元素插入到 DOM 中时调用
    bind: function (el, binding) {
        // 创建一个点击事件处理函数，并将它保存在元素的属性中
        const handleClick = event => handleClickOutside(event, el, binding)
        el.__vueClickOutside__ = handleClick

        // 在 document 上监听点击事件
        document.addEventListener('click', handleClick)
    },
    // 指令的解绑函数，在元素从 DOM 中移除时调用
    unbind(el) {
        // 移除之前保存在元素属性中的点击事件处理函数
        document.removeEventListener('click', el.__vueClickOutside__)
        delete el.__vueClickOutside__
    }
})
const handleClickOutside = (event, el, binding) => {
    // 检查点击的元素是否在绑定的元素内部
    let include = []
    if (binding.value && binding.value.include) {
        include = binding.value.include().filter(_ => _)
    }
    let isInclude = false
    if (include.length) {
        const arr = include.map(_ => _.contains(event.target))
        isInclude = arr.indexOf(true) > -1
    }
    if (!(el === event.target || el.contains(event.target) || isInclude)) {
        // 如果点击的元素不在绑定的元素内部，则触发绑定的回调函数
        if (binding.value && binding.value.handler) {
            if (binding.value.handler) {
                binding.value.handler()
            }
        } else {
            binding.value()
        }
    }
}

/**
 *  @description: 监听 DOM 元素尺寸变化
    <div 
      v-watchResize="handleResize" >
    ...
    </div>

    <div 
      v-watchResize="{
        handler: handleResize,
        spaceAfter: 10
      }" >
    ...
    </div>

    methdos: {
        handleResize(value) {}
    }

 */
Vue.directive('watchResize', {
    inserted: function (el) {
        // 元素初始化时的操作
        el.__resize__()
    },
    // componentUpdated: function (el) {
    //     // 内容变化时的操作
    //     el.__resize__()
    // },
    bind(el, binding) {
        el.__resize__ = event => handleWatchResize(event, el, binding)
        window.addEventListener('resize', el.__resize__)

        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (
                    mutation.type === 'childList' ||
                    mutation.type === 'characterData'
                ) {
                    el.__resize__()
                }
            })
        })
        observer.observe(el, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
            attributeOldValue: true
            // attributeFilter: ['style']
        })
        el.__observer__ = observer
    },
    unbind(el) {
        if (el.__observer__) {
            el.__observer__.disconnect()
        }
        el.removeEventListener('contextmenu', el.__resize__)
    }
})
const handleWatchResize = (event, el, binding) => {
    const style = document.defaultView.getComputedStyle(el)

    const tempDiv = document.createElement('div')
    tempDiv.style.position = 'absolute'
    tempDiv.style.visibility = 'hidden'
    tempDiv.style.height = 'auto'
    tempDiv.style.width = style.width
    tempDiv.style.fontSize = style.fontSize
    tempDiv.style.lineHeight = style.lineHeight
    tempDiv.style.fontWeight = style.fontWeight
    if (binding.value && binding.value.spaceAfter) {
        // 占位空格
        let place = ''
        for (let index = 0; index < binding.value.spaceAfter; index++) {
            place += String.fromCharCode(160)
        }
        tempDiv.innerHTML = `${el.textContent}${place}`
    } else {
        tempDiv.innerHTML = el.textContent
    }

    document.body.appendChild(tempDiv)
    const lineHeight = parseFloat(style.lineHeight)
    const textHeight = tempDiv.offsetHeight
    document.body.removeChild(tempDiv)

    const range = document.createRange()
    range.selectNodeContents(el)
    // const rect = range.getBoundingClientRect()
    // const textHeight = parseFloat(rect.height)
    const result = {
        width: parseFloat(style.width),
        height: parseFloat(style.height),
        textHeight: textHeight,
        lineHeight: lineHeight
    }
    if (binding.value && binding.value.handler) {
        binding.value.handler(result)
    } else {
        binding.value(result)
    }
}
